import axios from 'axios';
import { getAuth } from '../providers/AppAuthProvider';
import { baseUrl } from '../providers/AppDataProvider';
import { sanitizeUploadFilename } from 'src/utils/upload-filename';

async function getAuthHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

const prefix = `${baseUrl}/social-trading`;

/**
 * Verify userId exists in User Info module (GET /user-management/user-info/:userId).
 * Passes only when response has a non-null `data` payload; API errors / empty → fail.
 */
export async function stVerifyUserInfoExists(userId: string): Promise<boolean> {
  const id = userId.trim();
  if (!id) return false;
  try {
    const h = await getAuthHeaders();
    const { data } = await axios.get<any>(`${baseUrl}/user-management/user-info/${encodeURIComponent(id)}`, {
      headers: h,
    });
    return data?.data != null && typeof data.data === 'object';
  } catch {
    return false;
  }
}

/** Prefer backend `message` over Axios generic status text. */
export function stErrorMessage(error: unknown, fallback = 'Error'): string {
  const e = error as any;
  const data = e?.response?.data ?? e?.body;
  const fromErrors = Array.isArray(data?.errors)
    ? data.errors[0]?.message || (typeof data.errors[0] === 'string' ? data.errors[0] : null)
    : null;
  const candidates = [data?.message, fromErrors, data?.error, e?.message, fallback];
  const hasStructured = Boolean(data?.message || fromErrors);
  const picked = candidates.find(
    (c) =>
      typeof c === 'string' &&
      Boolean(c.trim()) &&
      // Skip generic axios text when a structured body exists but message was missed
      !(c.startsWith('Request failed with status code') && hasStructured),
  );
  return typeof picked === 'string' ? picked : fallback;
}

/**
 * Carries the server's error codes alongside the message.
 *
 * `code` is the BFF envelope code; `upstreamCode` is the field-level code the
 * social-trading GraphQL reported (V2.5 §5) — a save form maps that one onto
 * the field to highlight. Reading either from `message` would be guesswork.
 */
export class StApiError extends Error {
  readonly code?: string;

  readonly upstreamCode?: string;

  constructor(message: string, code?: string, upstreamCode?: string) {
    super(message);
    this.name = 'StApiError';
    this.code = code;
    this.upstreamCode = upstreamCode;
  }
}

function errorCodes(error: unknown): { code?: string; upstreamCode?: string } {
  const body = (error as any)?.response?.data ?? (error as any)?.body;
  const fromGraphql = Array.isArray(body?.errors)
    ? body.errors[0]?.extensions?.code
    : undefined;
  return {
    code: typeof body?.code === 'string' ? body.code : undefined,
    upstreamCode:
      (typeof body?.upstreamCode === 'string' ? body.upstreamCode : undefined) ??
      (typeof fromGraphql === 'string' ? fromGraphql : undefined),
  };
}

function toServiceError(error: unknown): Error {
  if (error instanceof StApiError) return error;
  const { code, upstreamCode } = errorCodes(error);
  return new StApiError(stErrorMessage(error), code, upstreamCode);
}

function unwrapOrThrow<T>(data: any): T {
  if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    throw new Error(data.errors[0]?.message || 'Error');
  }
  if (data?.code && data.code !== 'OK' && data.code !== 'SUCCESS' && data.message) {
    // error envelopes (including those that still attach a `data` payload like stack)
    if (typeof data.code === 'string' && (data.code.includes('ERROR') || data.code.includes('FAIL'))) {
      throw new Error(data.message || data.code);
    }
  }
  return data as T;
}

async function get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  try {
    const h = await getAuthHeaders();
    const { data } = await axios.get<any>(`${prefix}${path}`, { headers: h, params });
    return unwrapOrThrow<T>(data);
  } catch (error) {
    throw toServiceError(error);
  }
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  try {
    const h = await getAuthHeaders();
    const { data } = await axios.post<any>(`${prefix}${path}`, body ?? {}, { headers: h });
    return unwrapOrThrow<T>(data);
  } catch (error) {
    throw toServiceError(error);
  }
}

async function put<T>(path: string, body?: unknown): Promise<T> {
  try {
    const h = await getAuthHeaders();
    const { data } = await axios.put<any>(`${prefix}${path}`, body ?? {}, { headers: h });
    return unwrapOrThrow<T>(data);
  } catch (error) {
    throw toServiceError(error);
  }
}

async function del<T>(path: string): Promise<T> {
  try {
    const h = await getAuthHeaders();
    const { data } = await axios.delete<any>(`${prefix}${path}`, { headers: h });
    return unwrapOrThrow<T>(data);
  } catch (error) {
    throw toServiceError(error);
  }
}

// --- types ---
export interface StIdentityRole {
  code: string;
  name: string;
  description?: string;
  canBrowsePublicRoom: boolean;
  canJoinRoom: boolean;
  canSendMessage: boolean;
  canCreateRoom: boolean;
  canEditRoom: boolean;
  canConfigPool: boolean;
  canSendRedPacket: boolean;
  canClaimRedPacket: boolean;
  canPlaceOrder: boolean;
  canShowOfficialBadge: boolean;
}

export interface StIdentityUser {
  userId: string;
  nickname?: string;
  avatar?: string;
  role: string;
  banned: boolean;
  mutedAllRooms?: boolean;
  mutedAllRoomsAt?: string | null;
  mutedAllRoomsBy?: string | null;
  createdAt: string;
  updatedAt: string;
  userTradingVolume?: string;
}

export interface StAvatarDefaults {
  userAvatars: string[];
  roomOfficial: string;
  roomKol: string;
  roomAgent: string;
}

export interface StMemberMute {
  roomId: string;
  userId: string;
  mutedAt: string;
  mutedBy?: string | null;
  roomName?: string | null;
  groupId?: string | null;
}

export interface StRoomMember {
  id: string;
  roomId: string;
  userId: string;
  status: string;
  joinedAt?: string | null;
  leftAt?: string | null;
  notificationsEnabled: boolean;
  mutedAt?: string | null;
  mutedBy?: string | null;
  messageCount: number;
  roomName?: string | null;
  groupId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StReconcileRoomMembersResult {
  roomId: string;
  imMemberCount: number;
  markedActive: number;
  markedLeft: number;
  unchanged: number;
}

export interface StSignalStats {
  receivedToday: number;
  publishedToday: number;
  failedToday: number;
  skippedToday: number;
}

export interface StSignalEvent {
  id: string;
  signalId: string;
  agentId: string;
  agentName: string;
  strategyId: string;
  roomId?: string | null;
  roomName?: string;
  symbol: string;
  side: string;
  status: string;
  failureReason?: string | null;
  messageId?: string | null;
  createdAt: string;
  expiresAt?: string | null;
  validMinutes?: number | null;
  signalStatus?: 'ACTIVE' | 'EXPIRED' | null;
}

export interface StAgentActiveRoom {
  roomId: string;
  groupId: string;
  name: string;
  type: string;
  avatar?: string;
  ownerLabel: string;
  members: number;
}

export interface StAgentEquityPoint {
  date: string;
  value: number;
}

export interface StAgentReasoningEntry {
  title: string;
  detailCn: string;
  detailEn: string;
}

export interface StAgentDefaultProfile {
  source?: string;
  version?: string;
  productTags?: string[];
  riskLevel?: string;
  winRate?: number;
  sharpe?: number;
  maxDrawdown?: number;
  pnl30d?: number;
  sevenDayReturn?: number;
  fromBeginning?: number;
  symbol?: string;
  ohlcv?: string;
  equityCurve?: StAgentEquityPoint[];
  reasoning?: StAgentReasoningEntry[];
}

export interface StI18nEntry {
  lang: string;
  text: string;
}

export interface StFillI18nFieldResult {
  key: string;
  entries: StI18nEntry[];
}

export interface StAgent {
  agentId: string;
  name: string;
  avatar?: string;
  description?: string;
  strategyId?: string;
  strategyName?: string;
  strategyNameLang?: StI18nEntry[];
  descriptionLang?: StI18nEntry[];
  isActive: boolean;
  source?: string;
  version?: string;
  productTags?: string[];
  riskLevel?: string;
  winRate?: number;
  sharpe?: number;
  maxDrawdown?: number;
  pnl30d?: number;
  sevenDayReturn?: number;
  fromBeginning?: number;
  symbol?: string;
  ohlcv?: string;
  equityCurve?: StAgentEquityPoint[];
  reasoning?: StAgentReasoningEntry[];
  activeRoomCount: number;
  activeRooms?: StAgentActiveRoom[];
  botAccountId?: string;
  latestAgentSignalId?: string | null;
  profileSource?: 'ADMIN_DEFAULT' | 'LATEST_SIGNAL';
  defaultProfile?: StAgentDefaultProfile;
}

export interface StRoom {
  id: string;
  groupId: string;
  type: string;
  name: string;
  tagline: string;
  visibility: string;
  discoverable: boolean;
  accessPassword?: string;
  members: number;
  poolEnabled: boolean;
  /** On the user Smart Money widget. Optional so an older upstream without the field still renders. */
  smartMoney?: boolean;
  primaryAgentId?: string;
  hostedAgentIds: string[];
  ownerLabel: string;
  owner?: string;
  lang?: string;
  announcement?: string;
  announcementUpdatedAt?: string | null;
  /** Set while an async translation runs; informational, never a save gate. */
  announcementEditLockedUntil?: string | null;
  avatar?: string;
  preview?: string;
  max?: number;
  muteAll?: boolean;
  createdAt: string;
  deletedAt?: string | null;
  nameLang?: StI18nEntry[];
  taglineLang?: StI18nEntry[];
  previewLang?: StI18nEntry[];
  announcementLang?: StI18nEntry[];
}

export interface StPlatformBalance {
  poolType: string;
  totalBudget: string;
  availableBalance: string;
  spentBalance: string;
  frozenBalance: string;
  updatedAt: string;
}

export interface StClaimThreshold {
  enabled: boolean;
  minClaimAmount: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface StEligibilityThreshold {
  enabled: boolean;
  minTradingVolume: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface StSystemConfig {
  key: string;
  value: unknown;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StDisplayRulesLocale {
  title: string;
  rules: string[];
}

export interface StDisplayRules {
  cn: StDisplayRulesLocale;
  en: StDisplayRulesLocale;
}

export const REDPACKET_DISPLAY_RULES_KEY = 'redpacket.display_rules';

export function emptyDisplayRules(): StDisplayRules {
  return {
    cn: { title: '', rules: [] },
    en: { title: '', rules: [] },
  };
}

export interface StRedPacketPacket {
  id: string;
  name: string;
  roomId?: string;
  roomName: string;
  totalAmount: string;
  reservedAmount: string;
  claimedAmount: string;
  refundedAmount: string;
  claimCount: number;
  claimCap: number;
  thresholdLabel: string;
  sub: string;
  status: string;
  currency: string;
  createdAt: string;
  expiresAt: string;
}

export interface StRedPacketWithdrawal {
  id: string;
  userId: string;
  nickname?: string | null;
  amount: string;
  currency: string;
  targetAccount: string;
  status: string;
  createdAt: string;
  completedAt?: string | null;
  txHash?: string | null;
  failureReason?: string | null;
}

export interface StLedgerEntry {
  id: string;
  direction: string;
  userId?: string;
  nickname?: string;
  roomId?: string;
  roomName?: string;
  packetId?: string;
  amount: string;
  currency: string;
  balanceAfter?: string;
  memo?: string;
  poolType?: string;
  createdAt: string;
}

export interface StRoomPoolListItem {
  roomId: string;
  roomName: string;
  roomType: string;
  poolEnabled: boolean;
  /** Null when the room has no cycle yet — see `poolFormFromItem`. */
  poolId?: string | null;
  /**
   * V2.5 item 7. For a room with no cycle these come back as Go zero values
   * (`false` / null), which is not a configuration anyone chose — never bind
   * them straight into the form.
   */
  volumeEnabled?: boolean;
  profitEnabled?: boolean;
  requirePersonalVolume?: boolean;
  requirePersonalProfit?: boolean;
  /** Null on a cycle predating the feature: it still reads the global threshold. */
  minPersonalVolume?: string | null;
  minPersonalProfit?: string | null;
  cycleLabel?: string;
  cycleLabelLang?: StI18nEntry[];
  cycleStart?: string;
  cycleEnd?: string;
  volumeAmount?: string;
  profitAmount?: string;
  volumeTarget?: string;
  profitTarget?: string;
  status?: string;
}

export interface StSettlementCycle {
  id: string;
  roomId: string;
  roomName: string;
  cycleLabel: string;
  cycleLabelLang?: StI18nEntry[];
  status: string;
  cycleStart: string;
  cycleEnd: string;
  volumePoolAmount: string;
  profitPoolAmount: string;
  volumeDistributed: string;
  profitDistributed: string;
  volumeCurrent?: string;
  volumeTarget?: string;
  profitCurrent?: string;
  profitTarget?: string;
  participantCount: number;
}

export interface StPoolDashboard {
  totalPools: number;
  upcomingPools: number;
  activePools: number;
  endedPools: number;
  activeEnrolledUsers: number;
  totalVolume: string;
  totalProfit: string;
  configuredRewardTotal: string;
  settledRewardTotal: string;
}

export interface StPoolEnrollment {
  id: string;
  poolId: string;
  roomId: string;
  roomName: string;
  userId: string;
  enrolledAt: string;
  status: string;
  cycleStart: string;
  cycleEnd: string;
}

export interface StPoolContribution {
  id: string;
  poolId: string;
  roomId: string;
  roomName: string;
  userId: string;
  nickname: string;
  /** Account ban flag (ACTIVE | BANNED) — says nothing about pool participation. */
  userStatus: string;
  /**
   * ACTIVE | ENDED | RELEASED — tells apart the rows of a user who was released
   * and re-enrolled within one cycle. Null only when the enrollment record is gone.
   */
  enrollmentStatus?: string | null;
  volume: string;
  profit: string;
  estReward: string;
  finalReward: string;
  cycleStart: string;
  cycleEnd: string;
  updatedAt: string;
}

export interface StAuditLog {
  id: string;
  action: string;
  object: string;
  objectType: string;
  reason?: string | null;
  needsAction: boolean;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  roomId?: string | null;
  roomName?: string | null;
  fromValue?: string | null;
  toValue?: string | null;
  result: string;
  source: string;
  operator: string;
  createdAt: string;
  updatedAt: string;
}

export interface StOverview {
  claimThreshold: {
    enabled: boolean;
    minClaimAmount: string;
    updatedAt?: string | null;
    updatedBy?: string | null;
  };
  agentsActive: number;
  agentsInactive: number;
  exceptionsPending: number;
  exceptionsSignal: number;
  exceptionsRedPacket: number;
  poolsPendingSettlement: number;
  settlementBlockReason: string;
  todos: StAuditLog[];
}

export interface StAuditLogsQuery {
  keyword?: string;
  action?: string;
  result?: string;
  source?: string;
  from?: string;
  to?: string;
  openOnly?: boolean;
  limit?: number;
  offset?: number;
}

// --- identity ---
export const stIdentityRoles = () => get<StIdentityRole[]>('/identity/roles');
export const stIdentityUsers = (params?: {
  role?: string;
  nickname?: string;
  keyword?: string;
  limit?: number;
  offset?: number;
}) => get<{ items: StIdentityUser[]; total: number }>('/identity/users', params);
export const stIdentityUser = (userId: string) => get<StIdentityUser | null>(`/identity/users/${userId}`);
export const stSetIdentityUserRole = (body: { userId: string; role: string }) =>
  post<StIdentityUser>('/identity/users/role', body);
export const stSetIdentityUserMuteAllRooms = (body: { userId: string; muted: boolean }) =>
  post<StIdentityUser>('/identity/users/mute-all-rooms', body);
export const stMemberMutesByUser = (userId: string) =>
  get<StMemberMute[]>(`/identity/users/${userId}/member-mutes`);
export const stUpdateIdentityRolePermissions = (body: Partial<StIdentityRole> & { code: string }) =>
  post<StIdentityRole>('/identity/roles/permissions', body);
export const stUserRedpacketProfile = (userId: string) => get<any>(`/identity/users/${userId}/redpacket-profile`);
export const stBanUser = (body: { userId: string; banned: boolean }) =>
  post<StIdentityUser>('/identity/users/ban', body);
export const stUpdateIdentityUser = (body: { userId: string; nickname?: string; avatar?: string }) =>
  post<StIdentityUser>('/identity/users/update', body);
export const stCreateOfficialAccount = (body: { userId: string; nickname: string; avatar?: string }) =>
  post<StIdentityUser>('/identity/users/official', body);

export const stAvatarDefaults = () => get<StAvatarDefaults>('/avatar-defaults');
export const stUpsertAvatarDefaults = (body: StAvatarDefaults) =>
  post<StAvatarDefaults>('/avatar-defaults', body);

/** Upload avatar to CDN (pathPrefix social-trading/avatars). Returns public URL. */
export async function stUploadAvatar(
  file: File,
  uploadPath: '/upload' | '/identity/upload' | '/ledger/upload' = '/upload',
): Promise<string | undefined> {
  try {
    const h = await getAuthHeaders();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('customFilename', sanitizeUploadFilename(file.name));
    const { data } = await axios.post<any>(`${prefix}${uploadPath}`, formData, {
      headers: {
        Authorization: h.Authorization,
        Accept: 'application/json',
      },
    });
    const unwrapped = unwrapOrThrow<any>(data);
    return unwrapped?.file?.publicURL || unwrapped?.publicURL || unwrapped?.file?.cdnURL || unwrapped?.cdnURL;
  } catch (error) {
    throw toServiceError(error);
  }
}

// --- integration ---
export const stHealth = () => get<string>('/integration/health');
export const stListAgents = (params?: { activeOnly?: boolean; limit?: number; offset?: number }) =>
  get<{ items: StAgent[]; total: number }>('/integration/agents', params);
export const stCreateAgent = (body: {
  name: string;
  avatar?: string;
  description?: string;
  strategyId?: string;
  strategyName?: string;
  strategyNameLang: StI18nEntry[];
  descriptionLang: StI18nEntry[];
  botAccountId?: string;
  productTags?: string[];
  riskLevel?: string;
  version?: string;
  winRate?: number;
  sharpe?: number;
  maxDrawdown?: number;
  pnl30d?: number;
  sevenDayReturn?: number;
  fromBeginning?: number;
  symbol?: string;
  ohlcv?: string;
}) => post<{ agent: StAgent }>('/integration/agents', body);
export const stUpdateAgent = (body: {
  agentId: string;
  name?: string;
  avatar?: string;
  description?: string;
  strategyId?: string;
  strategyName?: string;
  strategyNameLang?: StI18nEntry[];
  descriptionLang?: StI18nEntry[];
  productTags?: string[];
  riskLevel?: string;
  version?: string;
  isActive?: boolean;
  winRate?: number;
  sharpe?: number;
  maxDrawdown?: number;
  pnl30d?: number;
  sevenDayReturn?: number;
  fromBeginning?: number;
  symbol?: string;
  ohlcv?: string;
  restoreDefaultProfile?: boolean;
}) => post<{ agent: StAgent }>('/integration/agents/update', body);
export const stListRooms = (params?: {
  type?: string;
  visibility?: string;
  limit?: number;
  offset?: number;
  includeDeleted?: boolean;
  roomId?: string;
  roomName?: string;
  smartMoney?: boolean;
}) => {
  const { includeDeleted, roomId, roomName, type, visibility, smartMoney, ...rest } = params ?? {};
  // Omit false — GET query strings cannot carry real booleans (Boolean("false")===true).
  // smartMoney is the exception: false is a real filter, so it is sent as 'false' and parsed server-side.
  return get<{ items: StRoom[]; total: number }>('/integration/rooms', {
    ...rest,
    ...(includeDeleted ? { includeDeleted: true } : {}),
    ...(roomId ? { roomId } : {}),
    ...(roomName ? { roomName } : {}),
    ...(type ? { type } : {}),
    ...(visibility ? { visibility } : {}),
    ...(typeof smartMoney === 'boolean' ? { smartMoney } : {}),
  });
};
export const stCreateRoom = (body: {
  ownerUserId: string;
  type: string;
  name: string;
  tagline: string;
  lang: string;
  visibility: string;
  discoverable?: boolean;
  announcement?: string;
  avatar?: string;
  preview?: string;
  max?: number;
  agentIds?: string[];
  nameLang: StI18nEntry[];
  taglineLang: StI18nEntry[];
  previewLang: StI18nEntry[];
  announcementLang: StI18nEntry[];
}) => post<StRoom>('/integration/rooms', body);
export const stUpdateRoom = (body: {
  roomId: string;
  name?: string;
  tagline?: string;
  lang?: string;
  visibility?: string;
  discoverable?: boolean;
  announcement?: string;
  announcementUpdatedAt?: string;
  avatar?: string;
  preview?: string;
  max?: number;
  agentIds?: string[];
  poolEnabled?: boolean;
  /** PUBLIC rooms only; upstream rejects it otherwise. */
  smartMoney?: boolean;
  poolConfig?: Record<string, unknown>;
  /** Transfer ownership; omitted keeps current owner. */
  ownerUserId?: string;
  nameLang?: StI18nEntry[];
  taglineLang?: StI18nEntry[];
  previewLang?: StI18nEntry[];
  announcementLang?: StI18nEntry[];
}) => post<StRoom>('/integration/rooms/update', body);
export const stDeleteRoom = (body: { roomId: string }) => post<StRoom>('/integration/rooms/delete', body);
export const stFillI18n = (fields: { key: string; source: string }[]) =>
  post<StFillI18nFieldResult[]>('/integration/i18n/fill', { fields });
export const stSetRoomMuteAll = (roomId: string, muted: boolean) =>
  post<StRoom>(`/integration/rooms/${roomId}/mute-all`, { muted });
export const stSetRoomMemberMute = (roomId: string, body: { userId: string; muted: boolean }) =>
  post<boolean>(`/integration/rooms/${roomId}/member-mute`, body);
export const stRoomMemberMutes = (roomId: string) =>
  get<StMemberMute[]>(`/integration/rooms/${roomId}/member-mutes`);
export const stListRoomMembers = (
  roomId: string,
  params?: { status?: string; userId?: string; limit?: number; offset?: number }
) => get<{ items: StRoomMember[]; total: number }>(`/integration/rooms/${roomId}/members`, params);
export const stReconcileRoomMembers = (roomId: string) =>
  post<StReconcileRoomMembersResult>(`/integration/rooms/${roomId}/members/reconcile`);
export const stBroadcastPush = (body: Record<string, unknown>) => post<any>('/integration/push/broadcast', body);
export const stSendRoomMessage = (body: Record<string, unknown>) => post<any>('/integration/push/room-message', body);
export const stPlatformBalances = () => get<StPlatformBalance[]>('/integration/redpacket/platform-balances');
export const stPlatformBalance = (poolType?: string) =>
  get<StPlatformBalance>('/integration/redpacket/platform-balance', { poolType });
export const stUpdatePlatformBudget = (body: { poolType?: string; totalBudget: string }) =>
  post<{ balance: StPlatformBalance }>('/integration/redpacket/platform-budget', body);
// --- signal monitor (real agent_signal) ---
export const stSignalStats = () => get<StSignalStats>('/integration/signal-stats');
export const stSignalEvents = (params?: {
  status?: string;
  signalId?: string;
  agentId?: string;
  roomId?: string;
  limit?: number;
  offset?: number;
}) => get<{ items: StSignalEvent[]; total: number }>('/integration/signal-events', params);

// --- ledger ---
export const stLedgerEntries = (params?: {
  direction?: string;
  scope?: string;
  userId?: string;
  packetId?: string;
  roomId?: string;
  roomName?: string;
  nickname?: string;
  limit?: number;
  offset?: number;
}) => get<{ items: StLedgerEntry[]; total: number }>('/ledger/entries', params);
export const stLedgerEntry = (id: string) => get<StLedgerEntry | null>(`/ledger/entries/${id}`);
export const stClaimThreshold = () => get<StClaimThreshold>('/ledger/claim-threshold');
export const stUpdateClaimThreshold = (body: { enabled: boolean; minClaimAmount?: string }) =>
  post<StClaimThreshold>('/ledger/claim-threshold', body);
export const stEligibilityThreshold = () => get<StEligibilityThreshold>('/ledger/eligibility-threshold');
export const stUpdateEligibilityThreshold = (body: { enabled: boolean; minTradingVolume?: string }) =>
  post<StEligibilityThreshold>('/ledger/eligibility-threshold', body);
export const stSystemConfigs = (params?: { keyPrefix?: string; limit?: number; offset?: number }) =>
  get<{ items: StSystemConfig[]; total: number }>('/ledger/system-configs', params);
export const stSystemConfig = (key: string) =>
  get<StSystemConfig | null>(`/ledger/system-configs/${encodeURIComponent(key)}`);
export const stUpsertSystemConfig = (body: { key: string; value: unknown }) =>
  post<StSystemConfig>('/ledger/system-configs', body);
export const stDeleteSystemConfig = (key: string) =>
  del<boolean>(`/ledger/system-configs/${encodeURIComponent(key)}`);
export const stDataPurgeCapability = () =>
  get<{ enabled: boolean }>('/ledger/data-purge/capability');
export const stPurgeAllData = (confirmPhrase: string) =>
  post<{ ok: boolean }>('/ledger/data-purge', { confirmPhrase });

// --- settlement ---
export type StPoolConfig = {
  minVolumeParticipation: string;
  minProfitParticipation: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export const stRoomPools = (params?: { limit?: number; offset?: number }) =>
  get<{ items: StRoomPoolListItem[]; total: number }>('/settlement/room-pools', params);
/** One reward source of a member's payout (V2.5 item 2). */
export interface StPoolRewardSource {
  /** "VOLUME" | "PROFIT" upstream, but typed as String there — treat it as open. */
  kind: string;
  amount: string;
  /** True when the member missed this pool's personal threshold; amount is then "0". */
  belowThreshold: boolean;
}

export interface StRoomPoolMember {
  userId: string;
  nickname: string;
  volume: string;
  profit: string;
  estReward: string;
  /**
   * One entry per enabled source, in `sources` order; a disabled source has no
   * entry. Empty on a cycle settled before this feature — the whole payout is
   * in `estReward` then.
   */
  rewardSources?: StPoolRewardSource[] | null;
  belowVolumeThreshold?: boolean;
  belowProfitThreshold?: boolean;
  rank: number;
}

export interface StRoomPoolDetail {
  roomId: string;
  poolId?: string | null;
  enabled?: boolean;
  status?: string;
  cycleLabel?: string;
  cycleStart?: string;
  cycleEnd?: string;
  members?: StRoomPoolMember[] | null;
  volumeEnabled?: boolean;
  profitEnabled?: boolean;
  minPersonalVolume?: string | null;
  minPersonalProfit?: string | null;
}

export const stRoomPool = (roomId: string) =>
  get<StRoomPoolDetail>(`/settlement/room-pools/${roomId}`);
export const stConfigurePool = (body: Record<string, unknown>) =>
  post<any>('/settlement/room-pools/configure', body);
export const stDisableRoomPool = (roomId: string) => post<any>(`/settlement/room-pools/${roomId}/disable`);
export const stPoolConfig = () => get<StPoolConfig>('/settlement/pool-config');
export const stUpdatePoolConfig = (body: {
  minVolumeParticipation?: string;
  minProfitParticipation?: string;
}) => post<StPoolConfig>('/settlement/pool-config', body);

export type StAgentsConfig = {
  cardValidHours: number;
  strategyIds: string[];
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export const stAgentsConfig = () => get<StAgentsConfig>('/agents-config');
export const stUpdateAgentsConfig = (body: { cardValidHours?: number; strategyIds?: string[] }) =>
  post<StAgentsConfig>('/agents-config', body);

export const stSettlementCycles = (params?: { status?: string; roomId?: string; limit?: number; offset?: number }) =>
  get<{ items: StSettlementCycle[]; total: number }>('/settlement/cycles', params);
export const stSettlementCycle = (id: string) => get<StSettlementCycle | null>(`/settlement/cycles/${id}`);
export const stPoolDashboard = () => get<StPoolDashboard>('/settlement/dashboard');
export const stPoolEnrollments = (params?: {
  poolId?: string;
  roomId?: string;
  userId?: string;
  roomName?: string;
  status?: string;
  limit?: number;
  offset?: number;
}) => get<{ items: StPoolEnrollment[]; total: number }>('/settlement/enrollments', params);
export const stReleasePoolEnrollment = (enrollmentId: string, reason: string) =>
  post<{ enrollment: StPoolEnrollment }>(`/settlement/enrollments/${enrollmentId}/release`, { reason });
export const stPoolContributions = (params?: {
  poolId?: string;
  roomId?: string;
  userId?: string;
  roomName?: string;
  limit?: number;
  offset?: number;
}) => get<{ items: StPoolContribution[]; total: number }>('/settlement/contributions', params);

export type StPoolRewardClaimStatus = 'CLAIMABLE' | 'CLAIMING' | 'CLAIMED';
export type StPoolRewardTargetAccount = 'PERP' | 'PREDICT_MARGIN' | 'PREDICT_GRID';

/** Shared by the stats and list endpoints; stats ignores limit/offset. Dates are ISO 8601. */
export interface StPoolRewardClaimFilter {
  roomId?: string;
  roomName?: string;
  poolId?: string;
  userId?: string;
  nickname?: string;
  claimStatus?: StPoolRewardClaimStatus;
  targetAccount?: StPoolRewardTargetAccount;
  cycleEndFrom?: string;
  cycleEndTo?: string;
  claimedFrom?: string;
  claimedTo?: string;
  limit?: number;
  offset?: number;
}

/**
 * Amounts are decimal strings passed through from upstream untouched — display
 * them as-is; never add them up as floats.
 */
export interface StPoolRewardClaimStats {
  totalFinalReward: string;
  totalClaimedAmount: string;
  /** CLAIMABLE + CLAIMING — money is only "claimed" once the credit completes. */
  totalUnclaimedAmount: string;
  claimedUsers: number;
  unclaimedUsers: number;
}

/** One settled contribution (user × cycle) with finalReward > 0. */
export interface StPoolRewardClaim {
  contributionId: string;
  userId: string;
  /** Snapshot taken at enrollment, not the user's current profile name. */
  nickname: string;
  roomId: string;
  /** Historical name — still present after the room is soft-deleted. */
  roomName: string;
  poolId: string;
  cycleLabel: string;
  cycleLabelLang?: StI18nEntry[] | null;
  cycleStart: string;
  cycleEnd: string;
  volumeReward: string;
  profitReward: string;
  finalReward: string;
  claimStatus: StPoolRewardClaimStatus;
  currency: string;
  // From the latest withdrawal of the contribution — null until one exists.
  claimedAmount: string | null;
  targetAccount: StPoolRewardTargetAccount | null;
  destinationRef: string | null;
  txHash: string | null;
  claimedAt: string | null;
  lastFailureReason: string | null;
}

export const stPoolRewardClaimStats = (params?: StPoolRewardClaimFilter) =>
  get<StPoolRewardClaimStats>('/settlement/reward-claims/stats', params as Record<string, unknown>);
export const stPoolRewardClaims = (params?: StPoolRewardClaimFilter) =>
  get<{ items: StPoolRewardClaim[]; total: number }>(
    '/settlement/reward-claims',
    params as Record<string, unknown>,
  );

// --- V2.5 item 8: user message statistics ---

/** `timeZone` is an IANA name; upstream never defaults it, so always send one. */
export interface StUserMessageStatsQuery {
  startDate: string;
  endDate: string;
  timeZone: string;
  roomId?: string;
  userId?: string;
}

export interface StUserMessageStatsRow {
  roomId: string;
  roomName: string;
  userId: string;
  nickname: string;
  /** Empty when no wallet is linked *or* the lookup failed — indistinguishable. */
  evmAddress: string;
  messageCount: number;
}

export interface StUserMessageStatsResult {
  rows: StUserMessageStatsRow[];
  /**
   * Timestamp of the newest message the log has ever recorded, across all
   * rooms — not the newest in the filtered range. Null means the log has never
   * recorded anything, which is what tells "no messages in this range" apart
   * from "no data at all". The log is not backfilled.
   */
  dataAsOf: string | null;
}

export const stUserMessageStats = (query: StUserMessageStatsQuery) =>
  get<StUserMessageStatsResult>('/message-stats', query as unknown as Record<string, unknown>);

/**
 * The CSV of the whole result set. The endpoint needs the bearer token, so it
 * cannot be opened with `window.open` or a plain link — it is fetched and
 * handed back as a blob.
 */
export async function stExportUserMessageStatsCsv(
  query: StUserMessageStatsQuery,
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== '') as [
      string,
      string,
    ][],
  );
  const headers = await getAuthHeaders();
  const res = await fetch(`${prefix}/message-stats/export?${params.toString()}`, {
    method: 'GET',
    headers: { ...headers, Accept: 'text/csv,*/*' },
  });

  if (!res.ok) {
    // Upstream filter errors arrive as JSON even on this endpoint.
    let message = `Export failed (${res.status})`;
    let code: string | undefined;
    try {
      const body = await res.json();
      message = body?.message || message;
      code = typeof body?.code === 'string' ? body.code : undefined;
    } catch {
      // Not JSON — keep the status-based message.
    }
    throw new StApiError(message, code);
  }

  const filename =
    /filename\s*=\s*"([^"]+)"/i.exec(res.headers.get('content-disposition') || '')?.[1] ||
    'message-stats.csv';
  return { blob: await res.blob(), filename };
}

// --- audit / overview (real upstream; no mock) ---
export const stAuditLogs = (params?: StAuditLogsQuery) =>
  get<{ items: StAuditLog[]; total: number }>('/audit/logs', params as Record<string, unknown>);
export const stResolveAuditLog = (id: string) => post<StAuditLog>(`/audit/logs/${id}/resolve`);
export const stOverview = () => get<StOverview>('/overview');

// --- red packet packets ---
export const stRedPacketPackets = (params?: {
  status?: string;
  roomName?: string;
  limit?: number;
  offset?: number;
}) => get<{ items: StRedPacketPacket[]; total: number }>('/ledger/redpacket-packets', params);
export const stForceExpireRedPacket = (packetId: string) =>
  post<StRedPacketPacket>(`/ledger/redpacket-packets/${packetId}/force-expire`);

export type StRedPacketReclaimStatus = 'RECLAIMED' | 'SKIPPED_ZERO' | 'NOT_FOUND' | 'FAILED';
export interface StRedPacketReclaimResult {
  input: string;
  userId: string | null;
  status: StRedPacketReclaimStatus;
  amount: string;
  balanceAmount: string;
  cancelledWithdrawalAmount: string;
  cancelledWithdrawalCount: number;
  processingWithdrawal: string;
  error: string | null;
}
export interface StRedPacketReclaimResponse {
  batchId: string;
  totalReclaimed: string;
  results: StRedPacketReclaimResult[];
}
export const stReclaimUserRedPacketBalances = (body: {
  userIds?: string[];
  walletAddresses?: string[];
  reason: string;
}) => post<StRedPacketReclaimResponse>('/ledger/redpacket-balances/reclaim', body);
export interface StRedPacketReclaimHistoryEntry {
  userId: string;
  amount: string;
  balanceAmount: string;
  cancelledWithdrawalCount: number;
}
export interface StRedPacketReclaimHistoryItem {
  id: string;
  batchId: string;
  createdAt: string;
  operator: string;
  reason: string;
  result: string;
  totalReclaimed: string;
  entries: StRedPacketReclaimHistoryEntry[];
}
export const stRedPacketReclaimHistory = (params: { limit: number; offset: number }) =>
  get<{ items: StRedPacketReclaimHistoryItem[]; total: number }>(
    '/ledger/redpacket-balances/reclaim-history',
    params
  );
export const stRedPacketWithdrawals = (params?: {
  status?: string;
  userId?: string;
  targetAccount?: string;
  limit?: number;
  offset?: number;
}) => get<{ items: StRedPacketWithdrawal[]; total: number }>('/ledger/redpacket-withdrawals', params);

// --- 7×24 intel ---
export type StIntel = {
  intelId: string;
  type: string;
  title: string;
  summary: string;
  sourceName: string;
  sourceUrl?: string | null;
  publishedAt: string;
  language: string;
  sentiment: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  tags: string[];
};

export type StIntelCategory = {
  code: string;
  name: string;
  sort: number;
};

export type StIntelCategoriesConfig = {
  categories: StIntelCategory[];
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export const stIntelCategories = () => get<StIntelCategory[]>('/integration/intel/categories');

export const stIntelCategoriesConfig = () =>
  get<StIntelCategoriesConfig>('/integration/intel/categories-config');

export const stUpdateIntelCategories = (body: { categories: StIntelCategory[] }) =>
  put<StIntelCategoriesConfig>('/integration/intel/categories-config', body);

export const stListIntels = (params?: {
  type?: string;
  tag?: string;
  intelId?: string;
  keyword?: string;
  language?: string;
  limit?: number;
  offset?: number;
}) => get<{ items: StIntel[]; offset: number; limit: number; total: number }>('/integration/intel', params);

export const stGetIntel = (intelId: string) =>
  get<StIntel | null>(`/integration/intel/${encodeURIComponent(intelId)}`);

export type StPublishIntelInput = {
  type: string;
  title: string;
  summary: string;
  sourceName: string;
  sourceUrl?: string;
  language: string;
  sentiment: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  tags?: string[];
};

export const stPublishIntel = (body: StPublishIntelInput) =>
  post<{ created: boolean; intel: StIntel }>('/integration/intel', body);

export const stUpdateIntel = (intelId: string, body: StPublishIntelInput) =>
  put<StIntel>(`/integration/intel/${encodeURIComponent(intelId)}`, body);

export const stDeleteIntel = (intelId: string) =>
  del<{ intelId: string; deleted: boolean }>(`/integration/intel/${encodeURIComponent(intelId)}`);

